import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { type FlowGeneratorSession, FlowItemType } from "@/types/flow.ts";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Chat } from "@/stores/useChatStore.ts";
import type { SocratesPedagogicalPlanJson } from "@/types/socrates.ts";
import { ADMIN_ENDPOINT, TEACHER_ENDPOINT } from "@/constants/settings.ts";
import type { QuizSessionDetailsResponse } from "@/api/quiz.ts";
import { AnswerToExplanation, LetterFromAnswer } from "@/helpers/answerMapper.ts";
import { clipboardShareSession } from "@/helpers/clipboard.ts";
import type { AssignedSession } from "@/stores/useAssignedChatStore.ts";
import { useSettingsStore } from "@/stores/useSettingsStore.ts";
import {
  getQuestionIdFromFlowItem,
  hasRenderableQuestionParts,
  type QuestionsById,
} from "@/helpers/questionParts.ts";
import { QuestionPartsContent } from "@/components/flowItems/QuestionPartsContent.tsx";
import { JsonParse } from "@/helpers/json.ts";
const Context = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  border-left: ${t.brandBorderSegment};
  width: 268px;
  flex-shrink: 0;
  background: white;
  gap: 1rem;
  overflow-y: auto;
  scrollbar-width: none;
  scrollbar-color: transparent transparent;
`;

const QuestionImage = styled.img`
  border: ${t.brandBorderSegment};
  border-radius: ${t.brandBorderRadius};
`;

const Group = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const Detail = styled.div`
  margin-bottom: 0.125rem;
  span {
    opacity: 0.6;
  }
`;

const Link = styled.a`
  color: ${t.brandTextPrimary};
  text-decoration: none;
  transition: all 150ms ease;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: ${t.brandPastelBlue};
  }
`;

const ContextDetails = styled.div``;

const Divider = styled.div`
  border-top: ${t.brandBorderSegment};
`;

interface ChatContextProps {
  chat: Chat;
  activeSession?: AssignedSession;
  className?: string;
  quizShortCode?: string;
  quizDataSession?: QuizSessionDetailsResponse;
  onClose?: () => void;
  questionsById?: QuestionsById;
}
//
// export const ChatTest = ({}) => {
//
//   useEffect(() => {
//     if (!contextRef.current) return;
//
//     const popout = window.open("", "", "width=400,height=500");
//     if (!popout) return;
//
//     popout.document.open();
//     popout.document.;
//   }, []);
//
//   return <div>ChatTest</div>;
// };

export const ChatContext: FC<ChatContextProps> = ({
  chat,
  activeSession,
  className,
  quizShortCode,
  quizDataSession,
  onClose,
  questionsById,
}) => {
  const { settings } = useSettingsStore();
  // Note: Forcing type as not undefined because ChatContext only renders if it's valid
  const flowSession = chat.flowGeneratorSession as FlowGeneratorSession;
  const socratesSession = chat.socrates.session;
  const contextRef = useRef<HTMLDivElement>(null);
  const popoutWindow = useRef<Window>(null);
  const [popoutContainer, setPopoutContainer] = useState<HTMLElement | null>(null);

  const image =
    chat.newMessages.find((data) => data.flowItemType === FlowItemType.PinnedImage) ??
    flowSession.flowGeneratorSession.flowItems.findLast(
      (data) => data.flowItemType === FlowItemType.PinnedImage,
    );
  const questionId = image ? getQuestionIdFromFlowItem(image) : null;
  const question = questionId ? questionsById?.get(questionId) : undefined;

  const questionNo = flowSession.flowGeneratorSession.flowItemGroupInputs.filter(
    (data) =>
      data.flowItemGroup.includes("CheckInQuestion_") &&
      !data.flowItemGroup.includes("_InterventionRating"),
  ).length;
  const yearGroup = flowSession.yearGroup?.replace("Year ", "");

  const currentQuestion = quizDataSession?.questions.find((q) => q.sequence === questionNo);

  const plan = useMemo(() => {
    const plan =
      socratesSession?.pedagogicalPlans &&
      socratesSession.pedagogicalPlans[socratesSession?.pedagogicalPlans.length - 1];

    if (plan) return JsonParse<SocratesPedagogicalPlanJson>(plan.planJson);

    return null;
  }, [socratesSession?.pedagogicalPlans]);

  useEffect(() => {
    if (!settings.ChatContextPopout || popoutWindow.current) return;

    const popout = window.open("", "", "width=400,height=500");
    if (!popout) return;

    popout.document.open();
    popout.document.write(
      "<!DOCTYPE html><html lang='en-GB'><head><title>Chat Context</title></head><body></body></html>",
    );
    popout.document.close();

    // Make the popout document fill the window and allow scrolling
    const baseStyle = popout.document.createElement("style");
    baseStyle.textContent = `
      html, body {
        margin: 0;
        padding: 0;
        height: 100%;
        width: 100%;
        overflow: visible !important;
      }
      body {
        display: flex;
        flex-direction: column;
      }
      body > div {
        width: 100% !important;
        height: auto !important;
        border-left: none !important;
        overflow: visible !important;
        scrollbar-width: none !important;
      }
    `;
    popout.document.head.appendChild(baseStyle);

    // Copy parent stylesheets so Linaria styles work in the popout
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        if (sheet.href) {
          const link = popout.document.createElement("link");
          link.rel = "stylesheet";
          link.href = sheet.href;
          popout.document.head.appendChild(link);
        } else if (sheet.cssRules) {
          const style = popout.document.createElement("style");
          style.textContent = Array.from(sheet.cssRules)
            .map((rule) => rule.cssText)
            .join("\n");
          popout.document.head.appendChild(style);
        }
      } catch {
        // Ignore CORS-restricted stylesheets
      }
    });

    popoutWindow.current = popout;
    // oxlint-disable-next-line react/set-state-in-effect
    setPopoutContainer(popout.document.body);

    const handleUnload = () => {
      popoutWindow.current = null;
      setPopoutContainer(null);
      if (onClose) onClose();
      popout.close();
    };
    popout.addEventListener("beforeunload", handleUnload);

    return () => {
      popout.removeEventListener("beforeunload", handleUnload);
      if (!popoutWindow.current) popout.close();
    };
  }, [onClose, settings.ChatContextPopout]);

  useEffect(() => {
    return () => {
      if (popoutWindow.current) {
        popoutWindow.current.close();
        popoutWindow.current = null;
      }
    };
  }, []);

  const content = (
    <Context ref={contextRef} className={className}>
      {question && hasRenderableQuestionParts(question) ? (
        <QuestionPartsContent compact question={question} />
      ) : (
        image?.imageUrl && (
          <QuestionImage alt={image.text || "Current question"} src={image.imageUrl} />
        )
      )}
      <ContextDetails>
        <Detail>
          <strong>Intervention type:</strong>
          <span>
            {" "}
            {chat.flowGeneratorSession?.isProactiveIntervention ? "Proactive" : "Manual"}
          </span>
        </Detail>
        <Detail>
          <Link onClick={() => clipboardShareSession(chat.flowGeneratorSessionId)}>
            <strong>Flow Session Id: </strong>
            <span> {chat.flowGeneratorSessionId}</span>
          </Link>
        </Detail>
        {activeSession && (
          <Detail>
            <strong>Intervention Id: </strong>
            <span> {activeSession.assignedChat.flowGeneratorSessionInterventionId}</span>
          </Detail>
        )}
        {settings.AdminEnabled && (
          <Detail>
            <Link
              href={ADMIN_ENDPOINT + "session-logs/" + flowSession.flowGeneratorSessionId}
              target="_blank"
            >
              <strong>Session Log: </strong>
              <span> {flowSession.flowGeneratorSessionId}</span>
            </Link>
          </Detail>
        )}
      </ContextDetails>
      <Divider />
      <ContextDetails>
        <Group>
          <Detail>
            <strong>User ID:</strong>
            <span> {flowSession.userId}</span>
          </Detail>
          <Detail>
            <strong>School ID:</strong>
            <span> {flowSession.student?.schoolId}</span>
          </Detail>
        </Group>
        <Detail>
          <strong>Username:</strong>
          <span> {flowSession.student?.userName}</span>
        </Detail>
        <Detail>
          <strong>First name:</strong>
          <span> {flowSession.student?.firstName}</span>
        </Detail>
        <Detail>
          <strong>Last name:</strong>
          <span> {flowSession.student?.lastName}</span>
        </Detail>
        <Detail>
          <strong>Year group:</strong>
          <span> {yearGroup}</span>
        </Detail>
      </ContextDetails>
      <Divider />
      <ContextDetails>
        <Detail>
          <Link
            href={quizShortCode ? TEACHER_ENDPOINT + "quizzes/" + quizShortCode : undefined}
            target="_blank"
            data-invalid={!quizShortCode}
            data-tooltip={
              !quizShortCode
                ? "Quiz not available, Retrieval quizzes are unique to this student"
                : undefined
            }
          >
            <strong>Quiz name:</strong>
            <span> {flowSession.quizName}</span>
          </Link>
        </Detail>
        <Detail>
          <Link href={TEACHER_ENDPOINT + "quizzes/" + quizShortCode} target="_blank">
            <strong>Quiz ID:</strong>
            <span> {flowSession.quizId}</span>
          </Link>
        </Detail>
        <Detail>
          <strong>Lesson ID:</strong>
          <span> {flowSession.lessonId}</span>
        </Detail>
        <Detail>
          <strong>Lesson type:</strong>
          <span> {flowSession.lessonType}</span>
        </Detail>
        <Detail>
          <strong>Check In Question No:</strong>
          <span> {questionNo}</span>
        </Detail>
        {currentQuestion && (
          <>
            <Detail>
              <strong>Check In Correct Answer:</strong>
              <span> {LetterFromAnswer(currentQuestion.correctAnswer)}</span>
            </Detail>
            <Detail>
              <strong>Check In Answer Explanation:</strong>
              <span> {AnswerToExplanation(currentQuestion.correctAnswer, currentQuestion)}</span>
            </Detail>
          </>
        )}
        {flowSession.metaData && (
          <Detail>
            <strong>Progress:</strong>
            <span> {flowSession.metaData.percentageComplete + "%"}</span>
          </Detail>
        )}
      </ContextDetails>

      {socratesSession && (
        <>
          <Divider />
          <ContextDetails>
            <Detail>
              <strong>Socrates Session ID:</strong>
              <span> {socratesSession.learnerSessionId}</span>
            </Detail>
            <Detail>
              <strong>Status:</strong>
              <span> {socratesSession.status}</span>
            </Detail>
            <br />
            {plan && (
              <>
                <Divider />
                <br />
                <ContextDetails>
                  <Detail>
                    <strong>Misconception:</strong>
                    <span> {plan.misconception}</span>
                  </Detail>
                  <Detail>
                    <strong>Error:</strong>
                    <span> {plan.error}</span>
                  </Detail>
                  <Detail>
                    <strong>Question:</strong>
                    <span> {plan.question}</span>
                  </Detail>
                  <Detail>
                    <strong>Strategy:</strong>
                    <span> {plan.strategy}</span>
                  </Detail>
                  <Detail>
                    <strong>Tone:</strong>
                    <span> {plan.tone}</span>
                  </Detail>
                  <Detail>
                    <strong>Why:</strong>
                    <span> {plan.why}</span>
                  </Detail>
                </ContextDetails>
              </>
            )}
          </ContextDetails>
        </>
      )}
    </Context>
  );

  return settings.ChatContextPopout
    ? popoutContainer && createPortal(content, popoutContainer)
    : content;
};
